import React, { useState } from 'react';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { PDFViewer } from './PDFViewer';
import { TextViewer } from './TextViewer';
import { MarkdownViewer } from './MarkdownViewer';
import {
  useRawFileContent,
  useProcessedFileContent,
  useFileMapping
} from '@/hooks/useFileManagement';
import { InputFileInfo } from '@/types/api';
import { useAPIClient } from '@/hooks/useAPIClient';

interface FileComparisonViewProps {
  selectedFile: InputFileInfo;
  onBack: () => void;
}

export function FileComparisonView({ selectedFile, onBack }: FileComparisonViewProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { toast } = useToast();
  const apiClient = useAPIClient();

  // Fetch raw file content (only for text files)
  const {
    data: rawFileData,
    isLoading: rawFileLoading,
    error: rawFileError
  } = useRawFileContent(
    selectedFile.file_type === 'txt' ? selectedFile.filepath : null
  );
  
  // Fetch file mapping to find processed files
  const { 
    data: mappingData, 
    isLoading: mappingLoading 
  } = useFileMapping(selectedFile.filepath);
  
  // Get the first processed file (assume there's usually one main processed file)
  const processedFileName = mappingData?.processed_files?.[0]?.filename;
  
  // Fetch processed file content
  const {
    data: processedFileData,
    isLoading: processedFileLoading,
    error: processedFileError
  } = useProcessedFileContent(processedFileName || null);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      // Use processed filename if available, otherwise use input filepath
      const fileToDelete = processedFileName || selectedFile.filepath;

      await apiClient.files.deleteDocument(fileToDelete);
      toast({
        title: "Dokument gelöscht",
        description: "Das Dokument wurde dauerhaft aus allen anwendbaren Speicherorten gelöscht.",
      });
      onBack(); // Navigate back to file list
    } catch (error) {
      console.error('Delete failed:', error);
      toast({
        title: "Löschen fehlgeschlagen",
        description: "Das Dokument konnte nicht gelöscht werden. Bitte versuchen Sie es erneut.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };


  const renderRawFileViewer = () => {
    if (selectedFile.file_type === 'pdf') {
      return (
        <PDFViewer 
          filepath={selectedFile.filepath}
          filename={selectedFile.filename}
        />
      );
    } else if (selectedFile.file_type === 'txt' && rawFileData) {
      return <TextViewer fileData={rawFileData} />;
    } else if (['docx', 'pptx'].includes(selectedFile.file_type)) {
      return (
        <Card className="h-full flex flex-col">
          <CardContent className="flex-1 flex items-center justify-center">
            <div className="text-center text-muted-foreground">
              <div className="mb-4">
                <div className="w-16 h-16 mx-auto bg-muted rounded-lg flex items-center justify-center">
                  <span className="text-2xl font-bold">
                    {selectedFile.file_type.toUpperCase()}
                  </span>
                </div>
              </div>
              <p className="font-medium">{selectedFile.filename}</p>
              <p className="text-sm mt-2">
                {selectedFile.file_type.toUpperCase()} files are not displayed in raw form.
              </p>
              <p className="text-sm">
                Use the processed version on the right to see the converted content.
              </p>
            </div>
          </CardContent>
        </Card>
      );
    } else if (rawFileLoading) {
      return <RawFileLoadingSkeleton />;
    } else if (rawFileError) {
      return (
        <Card className="h-full">
          <CardContent className="flex-1 flex items-center justify-center p-6">
            <Alert variant="destructive">
              <AlertDescription>
                Failed to load raw file: {rawFileError.message}
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      );
    }
    
    return (
      <Card className="h-full">
        <CardContent className="flex-1 flex items-center justify-center p-6">
          <div className="text-center text-muted-foreground">
            <p>Unable to display this file type</p>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderProcessedFileViewer = () => {
    if (processedFileLoading) {
      return <ProcessedFileLoadingSkeleton />;
    } else if (processedFileError) {
      return (
        <Card className="h-full">
          <CardContent className="flex-1 flex items-center justify-center p-6">
            <Alert variant="destructive">
              <AlertDescription>
                Failed to load processed file: {processedFileError.message}
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      );
    } else if (!processedFileData) {
      return (
        <Card className="h-full">
          <CardContent className="flex-1 flex items-center justify-center p-6">
            <div className="text-center text-muted-foreground">
              <div className="mb-4">
                <div className="w-16 h-16 mx-auto bg-muted rounded-lg flex items-center justify-center">
                  <span className="text-2xl">⏳</span>
                </div>
              </div>
              <p className="font-medium">Not Yet Processed</p>
              <p className="text-sm mt-2">
                This file hasn't been processed yet. Check back later or trigger processing manually.
              </p>
            </div>
          </CardContent>
        </Card>
      );
    }
    
    return <MarkdownViewer fileData={processedFileData} />;
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 p-4 border-b">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeft className="w-4 h-4" />
              Back to Files
            </Button>
            <div className="text-lg font-semibold">{selectedFile.filename}</div>
          </div>

          {/* Delete button - always show */}
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setShowDeleteConfirm(true)}
            disabled={isDeleting}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            {isDeleting ? 'Löschen...' : 'Löschen'}
          </Button>
        </div>
      </div>

      {/* Grid Split View */}
      <div className="flex-1 min-h-0">
        <div className="grid grid-cols-2 gap-4 h-full">
          {/* Left Panel - PDF File */}
          <div className="overflow-hidden">
            {renderRawFileViewer()}
          </div>

          {/* Right Panel - Processed Markdown */}
          <div className="overflow-auto">
            {renderProcessedFileViewer()}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Dokument löschen</AlertDialogTitle>
            <AlertDialogDescription>
              Dies wird "{selectedFile.filename}" dauerhaft aus allen anwendbaren Speicherorten löschen:
              <br />• Original-Eingabedatei
              {processedFileName && (
                <>
                  <br />• Verarbeitete Markdown- und Metadaten
                  <br />• Alle Einbettungen aus der Vektordatenbank
                </>
              )}
              {!processedFileName && (
                <>
                  <br />• Alle Staging-/Fortschrittsdateien (falls vorhanden)
                </>
              )}
              <br /><br />
              Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? 'Löschen...' : 'Löschen'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function RawFileLoadingSkeleton() {
  return (
    <Card className="h-full flex flex-col">
      <CardHeader>
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-64" />
      </CardHeader>
      <CardContent className="flex-1">
        <Skeleton className="h-full w-full" />
      </CardContent>
    </Card>
  );
}

function ProcessedFileLoadingSkeleton() {
  return (
    <Card className="h-full flex flex-col">
      <CardHeader>
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-64" />
      </CardHeader>
      <CardContent className="flex-1">
        <Skeleton className="h-full w-full" />
      </CardContent>
    </Card>
  );
}